import {
  assignmentFolderId,
  assignmentFolders,
  type Classroom,
  type HomeworkAssignment,
  type HomeworkRecord,
  type Student,
} from "@classroom/domain";
import { type ReactNode, useEffect, useRef, useState } from "react";
import "./coursework.css";
import GradeCategory from "./GradeCategory";
import { useTranslation } from "./i18n";
import { isExamCategory } from "./lib/assignmentRecordLabels";
import { courseworkSummary, needsCourseworkFollowup } from "./lib/courseworkSummary";
import { navigationId, useNavigationState } from "./lib/workspaceNavigation";
import { ToolPageToolbar } from "./ToolPageHeader";

export default function GradeOverview({
  termControl,
  classId,
  classroom,
  students,
  assignments,
  records,
  onOpenAssignment,
  onOpenTable,
  onCompareTerms,
  followupRequest = 0,
}: {
  termControl?: ReactNode;
  classId: string;
  classroom: Classroom;
  students: Student[];
  assignments: HomeworkAssignment[];
  records: HomeworkRecord[];
  onOpenAssignment: (id: string) => void;
  onOpenTable: (id?: string) => void;
  onCompareTerms: () => void;
  followupRequest?: number;
}) {
  const { t } = useTranslation("gradeOverview");
  const [selectedFolder, setFolder] = useNavigationState(`gradeOverview:${classId}:folder`, "", navigationId);
  const [passInput, setPassInput] = useState("");
  const followupHeading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (!followupRequest) return;
    setFolder("");
    followupHeading.current?.focus();
    followupHeading.current?.scrollIntoView({ block: "nearest" });
  }, [followupRequest, setFolder]);
  const folders = assignmentFolders(classroom);
  const { t: folderCopy } = useTranslation("assignmentRecords");
  const options = [
    { value: "", label: t("categories.all") },
    ...folders.map((folder) => ({ value: folder.id, label: folder.name })),
  ];
  if (assignments.some((item) => item.classId === classId && assignmentFolderId(item, folders) === "uncategorized"))
    options.push({ value: "uncategorized", label: folderCopy("folders.uncategorized") });
  const category = options.some((option) => option.value === selectedFolder) ? selectedFolder : "";
  const filtered = assignments.filter(
    (item) => item.classId === classId && (!category || assignmentFolderId(item, folders) === category),
  );
  const passLine =
    passInput !== "" && Number.isFinite(Number(passInput)) && Number(passInput) >= 0 && Number(passInput) <= 100
      ? Number(passInput)
      : undefined;
  const date = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Hong_Kong" });
  const rows = courseworkSummary(classId, students, filtered, records, date, passLine);
  const attention = rows.filter(needsCourseworkFollowup);
  const recent = rows
    .filter((row) => isExamCategory(row.assignment.category) && row.assignment.dueDate <= date)
    .slice(0, 3);
  const concerning = rows.filter((row) => row.below > 0);
  const totals = rows.reduce(
    (sum, row) => ({
      pending: sum.pending + row.pending,
      missing: sum.missing + row.missing,
      unrecorded: sum.unrecorded + row.unrecorded,
    }),
    { pending: 0, missing: 0, unrecorded: 0 },
  );
  return (
    <div className="coursework-dashboard">
      <h3 className="sr-only">{t("dashboard.title")}</h3>
      <ToolPageToolbar
        scope={
          <>
            {termControl}
            <GradeCategory value={category} onChange={setFolder} options={options} />
            <span className="coursework-meta">{t("assignments.count", { count: rows.length })}</span>
          </>
        }
        actions={
          <>
            <button
              id="coursework-term-comparison-trigger"
              type="button"
              aria-haspopup="dialog"
              onClick={onCompareTerms}
            >
              {t("dashboard.compareTerms")}
            </button>
            <button type="button" onClick={() => onOpenTable()}>
              {t("dashboard.openTable")}
            </button>
          </>
        }
      />
      <div className="coursework-stats">
        {[
          ["pending", totals.pending],
          ["missing", totals.missing],
          ["unrecorded", totals.unrecorded],
        ].map(([kind, count]) => (
          <section key={kind} className="coursework-stat">
            <span>{t(`dashboard.${kind as "pending" | "missing" | "unrecorded"}`)}</span>
            <strong>
              {count}
              <small>{t("dashboard.recordsUnit")}</small>
            </strong>
          </section>
        ))}
      </div>
      <section className="coursework-attention" aria-labelledby="coursework-attention-title">
        <header className="coursework-section-header">
          <h4 id="coursework-attention-title" ref={followupHeading} tabIndex={-1}>
            {t("dashboard.attention")}
          </h4>
          <span>{t("dashboard.taskCount", { count: attention.length })}</span>
        </header>
        <div
          className="coursework-followups"
          role="region"
          aria-label={t("dashboard.followupRegion")}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: Keyboard users need to scroll the follow-up region.
          tabIndex={0}
        >
          {attention.map((row) => (
            <button
              type="button"
              className="coursework-followup"
              key={row.assignment.id}
              aria-label={t("dashboard.openRecord", { title: row.assignment.title })}
              onClick={() => onOpenAssignment(row.assignment.id)}
            >
              <span className="coursework-task-title">
                <b>{row.assignment.title}</b>
                <small>{row.assignment.dueDate}</small>
              </span>
              <span className="coursework-badges">
                {row.missing > 0 && (
                  <span className="coursework-badge coursework-badge-alert">
                    {t(isExamCategory(row.assignment.category) ? "dashboard.absentCount" : "dashboard.missingCount", {
                      count: row.missing,
                    })}
                  </span>
                )}
                {row.pending > 0 && (
                  <span className="coursework-badge">{t("dashboard.pendingCount", { count: row.pending })}</span>
                )}
                {row.unrecorded > 0 && (
                  <span className="coursework-badge coursework-badge-muted">
                    {t("dashboard.unrecordedCount", { count: row.unrecorded })}
                  </span>
                )}
              </span>
              <span aria-hidden="true">→</span>
            </button>
          ))}
          {attention.length === 0 && (
            <p className="coursework-empty">{t(rows.length ? "dashboard.clear" : "assignments.empty")}</p>
          )}
        </div>
      </section>
      <section className="coursework-concerns" aria-labelledby="coursework-concerns-title">
        <header className="coursework-section-header">
          <h4 id="coursework-concerns-title">{t("dashboard.concerns")}</h4>
          <label className="coursework-passline">
            <span>{t("dashboard.passLine")}</span>
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={passInput}
              placeholder={t("dashboard.passPlaceholder")}
              aria-label={t("dashboard.passLine")}
              onChange={(event) => setPassInput(event.target.value)}
            />
            <span>%</span>
          </label>
        </header>
        {passInput !== "" && passLine === undefined ? (
          <p role="alert">{t("dashboard.invalidPassLine")}</p>
        ) : passLine === undefined ? (
          <p className="coursework-empty">{t("dashboard.noPassLine")}</p>
        ) : concerning.length === 0 ? (
          <p className="coursework-empty">{t("dashboard.noConcerns")}</p>
        ) : (
          <div className="coursework-concern-list">
            {concerning.map((row) => (
              <button type="button" key={row.assignment.id} onClick={() => onOpenTable(row.assignment.id)}>
                <b>{row.assignment.title}</b>
                <span>{t("dashboard.belowCount", { count: row.below })}</span>
                <span aria-hidden="true">→</span>
              </button>
            ))}
          </div>
        )}
      </section>
      <section aria-labelledby="coursework-recent-title">
        <header className="coursework-section-header">
          <h4 id="coursework-recent-title">{t("dashboard.recent")}</h4>
        </header>
        <div className="coursework-recent-grid">
          {recent.map((row) => (
            <article className="coursework-evaluation" key={row.assignment.id}>
              <header>
                <h5>{row.assignment.title}</h5>
                <span>{row.assignment.dueDate}</span>
              </header>
              <div className="coursework-evaluation-average">
                <strong>{row.average === undefined ? "—" : `${Math.round(row.average)}%`}</strong>
                <span>{t("dashboard.average")}</span>
              </div>
              <p className="coursework-meta">{t("dashboard.assessed", { count: row.scored })}</p>
              <div className="coursework-distribution">
                {row.distribution.map((band) => (
                  <div key={band.label}>
                    <span>{band.label}</span>
                    <div aria-hidden="true">
                      <i style={{ width: `${row.scored ? (band.count / row.scored) * 100 : 0}%` }} />
                    </div>
                    <b>{band.count}</b>
                  </div>
                ))}
              </div>
              {row.assignment.maxScore === undefined && <p className="coursework-meta">{t("dashboard.noMaximum")}</p>}
              <button type="button" onClick={() => onOpenTable(row.assignment.id)}>
                {t("dashboard.viewScores")}
              </button>
            </article>
          ))}
        </div>
        {recent.length === 0 && <p className="coursework-empty">{t("dashboard.noEvaluations")}</p>}
      </section>
    </div>
  );
}
